import { useState, useCallback, useEffect } from 'react';
import { type ArticuloRequest, type ArticuloFormErrors } from '../types/articulo';
import { ArticuloValidation } from '../services/articuloValidation';

interface UseArticuloFormProps {
  initialArticuloRequest: ArticuloRequest | null;
  isOpen: boolean;
  onSubmit: (articuloRequest: ArticuloRequest) => void;
}

export const useArticuloForm = ({
  initialArticuloRequest,
  isOpen,
  onSubmit,
}: UseArticuloFormProps) => {
  const [formData, setFormData] = useState<ArticuloRequest>({
    descripcion: '',
    codigo: '',
    marca: '',
    modelo: '',
    ubicacion: '',
    precio: 0,
    idEstado: 1,
    servicio: false,
  });
  const [errors, setErrors] = useState<ArticuloFormErrors>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDirty, setIsDirty] = useState(false);

  // Reset form when dialog opens/closes or when initial data changes
  useEffect(() => {
    if (isOpen) {
      const initialData: ArticuloRequest = initialArticuloRequest || {
        descripcion: '',
        codigo: '',
        marca: '',
        modelo: '',
        ubicacion: '',
        precio: 0,
        idEstado: 1,
        servicio: false,
      };
      setFormData(initialData);
      setErrors({});
      setIsDirty(false);
    }
  }, [isOpen, initialArticuloRequest]);

  const handleFieldChange = useCallback((field: keyof ArticuloRequest, value: any) => {
    setFormData(prev => {
      const newData = { ...prev, [field]: value };
      return newData;
    });
    setIsDirty(true);

    // Clear error for this field
    if (errors[field as keyof ArticuloFormErrors]) {
      setErrors(prev => ({ ...prev, [field]: undefined }));
    }
  }, [errors]);

  const validateForm = useCallback((): boolean => {
    const validationErrors = ArticuloValidation.validateArticulo(formData);
    setErrors(validationErrors);
    return !ArticuloValidation.hasValidationErrors(validationErrors);
  }, [formData]);

  const handleSubmit = useCallback(async () => {
    if (!validateForm() || isSubmitting) {
      return;
    }

    setIsSubmitting(true);
    try {
      await onSubmit(formData);
      setIsDirty(false);
    } catch (error) {
      console.error('Error submitting articulo form:', error);
    } finally {
      setIsSubmitting(false);
    }
  }, [formData, validateForm, onSubmit, isSubmitting]);

  const isValid = Object.keys(errors).length === 0 && formData.descripcion.trim() !== '';

  return {
    formData,
    errors,
    isValid,
    isDirty,
    isSubmitting,
    handleFieldChange,
    handleSubmit,
  };
};